import React, { useEffect, useRef, useState } from 'react';

import { CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Drawer } from 'antd';
import { colors } from 'theme/colors';

import { ChatConversation } from 'pages/chat/shared';
import { useStreamingChat } from 'pages/chat/useStreamingChat';

import { Button } from 'components/Button';

import ChatIcon from 'assets/svgs/cwd-chat-assistant-icon.svg';
import DragHandleIcon from 'assets/svgs/cwd-chat-drag-handle.svg';
import EraserIcon from 'assets/svgs/cwd-chat-eraser.svg';
import ResizeGripIcon from 'assets/svgs/cwd-chat-resize-grip.svg';

import { formatPageContext, useChatDrawer, useChatRegistry } from './ChatContext';

const DRAWER_WIDTH = 460;

const MARGIN = 8;

// Keep at least this much of the panel on screen so the drag handle stays grabbable.
const MIN_VISIBLE = 80;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;

const sideResizeHandleBase = css`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  touch-action: none;
  z-index: 10;
`;
// Corners resize width + height together; sit above the edge handles at overlaps.
const cornerHandleBase = css`
  position: absolute;
  width: 14px;
  height: 14px;
  touch-action: none;
  z-index: 11;
`;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type DragMode = 'move' | 'left' | 'right' | 'top' | 'bottom' | 'bottomLeft' | 'bottomRight';

/**
 * Owns the drawer panel's position (offset from the right/top dock) and size, plus
 * the pointer handlers for moving it (title bar) and resizing it from the left edge,
 * right edge, or bottom edge. Position and size reset each time the drawer opens.
 *
 * The panel is right-docked, so the two width edges behave differently: the left
 * handle grows the panel leftward (right edge fixed), while the right handle keeps the
 * left edge fixed by shifting the anchor as the width changes — which is why position
 * and size live together here.
 */
function useDrawerBox(isOpen: boolean) {
  const [offset, setOffset] = useState({ x: -MARGIN, y: MARGIN });
  const [size, setSize] = useState<{ width: number; height?: number }>(() => ({
    width: DRAWER_WIDTH,
    height: window.innerHeight - MARGIN * 2,
  }));
  const [mode, setMode] = useState<DragMode | null>(null);
  const origin = useRef({ px: 0, py: 0, ox: 0, oy: 0, ow: DRAWER_WIDTH, oh: 0 });

  useEffect(() => {
    if (isOpen) {
      setOffset({ x: -MARGIN, y: MARGIN });
      setSize({ width: DRAWER_WIDTH, height: window.innerHeight - MARGIN * 2 });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!mode) return undefined;
    const onMove = (e: PointerEvent) => {
      const { px, py, ox, oy, ow, oh } = origin.current;
      const dx = e.clientX - px;
      const dy = e.clientY - py;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (mode === 'move') {
        setOffset({
          // x<0 moves the right-docked panel left; cap so it can't slide fully off either edge.
          x: clamp(ox + dx, -(vw - DRAWER_WIDTH), DRAWER_WIDTH - MIN_VISIBLE),
          y: clamp(oy + dy, 0, vh - MIN_VISIBLE),
        });
        return;
      }

      // Edges and corners are combinations of four independent edge effects.
      const affectsLeft = mode === 'left' || mode === 'bottomLeft';
      const affectsRight = mode === 'right' || mode === 'bottomRight';
      const affectsBottom = mode === 'bottom' || mode === 'bottomLeft' || mode === 'bottomRight';
      const affectsTop = mode === 'top';

      if (affectsLeft) {
        // Right edge fixed; grow leftward. Cap so the left edge can't pass the viewport left.
        setSize((s) => ({ ...s, width: clamp(ow - dx, MIN_WIDTH, vw + ox) }));
      } else if (affectsRight) {
        // Keep the left edge fixed. Cap width so the right edge can't pass the viewport
        // right (ow - ox), then shift the anchor by the applied delta.
        const width = clamp(ow + dx, MIN_WIDTH, ow - ox);
        setSize((s) => ({ ...s, width }));
        setOffset((o) => ({ ...o, x: ox + (width - ow) }));
      }
      if (affectsBottom) {
        // Top fixed; drag the bottom edge to change height.
        setSize((s) => ({ ...s, height: clamp(oh + dy, MIN_HEIGHT, vh) }));
      } else if (affectsTop) {
        // Bottom edge fixed; move the top down to shrink, shifting the anchor to match.
        const bottom = oy + oh;
        const top = clamp(oy + dy, 0, bottom - MIN_HEIGHT);
        setOffset((o) => ({ ...o, y: top }));
        setSize((s) => ({ ...s, height: bottom - top }));
      }
    };
    const onUp = () => setMode(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [mode]);

  const start = (nextMode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    origin.current = {
      px: e.clientX,
      py: e.clientY,
      ox: offset.x,
      oy: offset.y,
      ow: size.width,
      oh: size.height ?? window.innerHeight,
    };
    setMode(nextMode);
  };

  return {
    offset,
    size,
    onMovePointerDown: start('move'),
    startLeftResize: start('left'),
    startRightResize: start('right'),
    startTopResize: start('top'),
    startBottomResize: start('bottom'),
    startBottomLeftResize: start('bottomLeft'),
    startBottomRightResize: start('bottomRight'),
  };
}

function AssistantEmptyState() {
  return (
    <>
      <div
        className={css`
          width: 48px;
          height: 48px;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background: ${colors.primary[100]};
        `}
      >
        <ChatIcon width={24} height={24} />
      </div>
      <div style={{ fontSize: '1.1em', marginBottom: 8, fontWeight: 600, color: colors.neutral[800] }}>
        Ask about what you&apos;re seeing
      </div>
      <div style={{ fontSize: '0.95em', color: colors.neutral[500], lineHeight: 1.5 }}>
        Questions run against this page, including the filters you have applied. Change the page and the context
        follows.
      </div>
    </>
  );
}

/**
 * Slide-out chat drawer, mounted once in the app shell (only for users with the feature). Opened
 * from the header launcher (see PrimaryNavigation) via shared open-state on ChatContext. Seeds each
 * question with the current page's context and keeps a rolling local history (last 30 exchanges,
 * tenant+user-scoped) so closing/reopening or refreshing restores recent context; the clear button
 * (reset) wipes it.
 */
export function ChatAssistant() {
  const drawer = useChatDrawer();
  const isOpen = !!drawer?.isChatOpen;
  const registry = useChatRegistry();
  const { messages, isLoading, sendMessage, reset } = useStreamingChat({
    persistHistory: false,
    rollingHistoryKey: 'PageChat',
    rollingHistoryLimit: 30,
    getQuestionContext: registry ? () => formatPageContext(registry.collectPageContext()) : undefined,
  });
  const {
    offset,
    size,
    onMovePointerDown,
    startLeftResize,
    startRightResize,
    startTopResize,
    startBottomResize,
    startBottomLeftResize,
    startBottomRightResize,
  } = useDrawerBox(isOpen);

  return (
    <Drawer
      title={
        <>
          {/* Put on top of the title bar so user can resize from the actual top */}
          <div
            className={css`
              position: absolute;
              left: 44px;
              right: 52px;
              top: 0;
              height: 6px;
              cursor: ns-resize;
              touch-action: none;
              z-index: 12;
            `}
            onPointerDown={startTopResize}
          />
          {/* allow title to drag to move modal around */}
          <div
            className={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
            `}
          >
            <div
              className={css`
                display: flex;
                align-items: center;
                gap: 8px;
                cursor: move;
                user-select: none;
                touch-action: none;
              `}
              onPointerDown={onMovePointerDown}
            >
              <DragHandleIcon width="1em" height="1em" style={{ color: colors.neutral[400] }} />
              Cue
            </div>
            <div
              className={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <Button
                size="small"
                type="text"
                icon={<EraserIcon width="1em" height="1em" style={{ verticalAlign: '-0.125em' }} />}
                onClick={reset}
                disabled={messages.length === 0}
              />
              <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => drawer?.closeChat()} />
            </div>
          </div>
        </>
      }
      // We render our own close in the title, so hide antd's default (left-docked) close icon.
      closable={false}
      className={css`
        /* With mask={false}, antd puts the drawer's width on the fixed, full-height root element
           instead of the content wrapper (getRcDrawerStyle in antd/es/drawer/index.js). That root
           stays docked at the right edge no matter where we move the panel, so it keeps swallowing
           clicks on whatever sits under the panel's starting position. Let pointers through the
           root and turn them back on for the panel itself. */
        pointer-events: none;

        .ant-drawer-content-wrapper {
          pointer-events: auto;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 12px 40px rgba(24, 24, 40, 0.18), 0 2px 10px rgba(24, 24, 40, 0.08);
        }
        .ant-drawer-header {
          background: ${colors.neutral[75]};
          border-bottom: 1px solid ${colors.neutral[200]};
          padding: 10px 16px;
          position: relative;
        }
        .ant-drawer-title {
          font-weight: 600;
          color: ${colors.neutral[800]};
          overflow: visible;
        }
      `}
      placement="right"
      width={size.width}
      visible={isOpen}
      onClose={() => drawer?.closeChat()}
      // No destroyOnClose: keep the panel mounted so closing/reopening preserves the
      // conversation in memory (a page refresh restores it from the rolling store).
      // Non-modal so the page stays visible/interactive while the panel is moved around.
      mask={false}
      // Reposition via margins (relative to the right/top dock) so we don't clobber
      // antd's open/close transform animation. offset.x<0 shifts the panel left, so
      // marginRight = -offset.x is the gap from the right edge.
      contentWrapperStyle={{ marginTop: offset.y, marginRight: -offset.x, height: size.height }}
      bodyStyle={{ padding: 0, height: '100%' }}
    >
      <div
        className={css`
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          className={css`
            ${sideResizeHandleBase};
            left: 0;
          `}
          onPointerDown={startLeftResize}
        />
        <div
          className={css`
            ${sideResizeHandleBase};
            right: 0;
          `}
          onPointerDown={startRightResize}
        />
        <div
          className={css`
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 6px;
            cursor: ns-resize;
            touch-action: none;
            z-index: 10;
          `}
          onPointerDown={startBottomResize}
        />
        <div
          className={css`
            ${cornerHandleBase};
            left: 0;
            bottom: 0;
            cursor: nesw-resize;
          `}
          onPointerDown={startBottomLeftResize}
        />
        <div
          className={css`
            ${cornerHandleBase};
            right: 0;
            bottom: 0;
            // Larger than the base 14px so the 9px grip fits without the flex box shrinking it,
            // while the bottom/right padding insets its tip ~6px off the corner — clear of the
            // panel's 14px rounded corner.
            width: 18px;
            height: 18px;
            cursor: nwse-resize;
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;
            padding: 0 6px 6px 0;
            color: ${colors.neutral[400]};

            & > svg {
              flex-shrink: 0;
            }
          `}
          onPointerDown={startBottomRightResize}
        >
          <ResizeGripIcon width={9} height={9} />
        </div>
        <div
          className={css`
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            font-size: 14px;
            color: ${colors.success[700]};
            background: ${colors.success[100]};
            border-bottom: 1px solid ${colors.success[200]};
          `}
        >
          <EyeOutlined />
          Reading the page you have open
        </div>
        <div
          className={css`
            flex: 1;
            min-height: 0;
          `}
        >
          <ChatConversation
            messages={messages}
            isLoading={isLoading}
            onSend={sendMessage}
            placeholder="Why is this item flagged?"
            emptyState={<AssistantEmptyState />}
          />
        </div>
      </div>
    </Drawer>
  );
}
